import { clearServiceWorkerAndCaches } from '../clearServiceWorkerAndCaches';
import { getErrorMessage, isStaleClientAssetErrorMessage } from './isStaleClientAssetError';

export const CHUNK_RELOAD_STORAGE_KEY = 'guarnic_chunk_reload_once';

/** In-memory fallback when sessionStorage is unavailable (private mode, quota, etc.). */
let recoveredThisPage = false;

type ReloadOnceOnChunkLoadFailureOptions = {
  clearCaches?: () => Promise<void>;
  reload?: () => void;
};

/**
 * Clears SW + Cache Storage then reloads once when a stale hashed asset fails
 * (chunk JS/CSS preload / dynamic import — typical after deploy when the
 * controlling SW still serves an old shell). Soft reload alone often leaves
 * the user stuck; aggressive clear matches the manual "clear site data" path.
 */
const reloadOnceOnChunkLoadFailure = (
  reason: unknown,
  {
    clearCaches = clearServiceWorkerAndCaches,
    reload = () => window.location.reload(),
  }: ReloadOnceOnChunkLoadFailureOptions = {},
): boolean => {
  const message = getErrorMessage(reason);

  if (!isStaleClientAssetErrorMessage(message)) {
    return false;
  }

  try {
    if (sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY)) {
      return false;
    }
    sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, '1');
  } catch {
    if (recoveredThisPage) {
      return false;
    }
    recoveredThisPage = true;
  }

  void clearCaches()
    .catch(() => undefined)
    .finally(() => {
      reload();
    });

  return true;
};

export { reloadOnceOnChunkLoadFailure };

/** Test-only: reset in-memory one-shot guard. */
export const __resetChunkReloadForTests = (): void => {
  recoveredThisPage = false;
};
